import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import gibletLogo from '@/assets/giblet-logo.png';
import { format, addDays } from 'date-fns';

export default function DemoCallForDestructionOutcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const bountyAmount = state.bountyAmount || 2500;
  const gibletShare = bountyAmount * 0.01;
  const communityShare = bountyAmount * 0.99;

  const today = new Date();
  const timelineSteps = [
    { label: 'CfD Submitted', date: format(today, 'MMM d, yyyy'), status: 'complete' },
    { label: 'KB Owner Notified', date: format(today, 'MMM d, yyyy'), status: 'complete' },
    { label: 'Response Window Open', date: `${format(today, 'MMM d')} — ${format(addDays(today, 7), 'MMM d, yyyy')}`, status: 'active' },
    { label: 'Arbiter Review', date: format(addDays(today, 8), 'MMM d, yyyy'), status: 'pending' },
    { label: 'Resolution & Token Release', date: format(addDays(today, 10), 'MMM d, yyyy'), status: 'pending' },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-2xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">CfD Outcome Summary</h1>
          <p className="text-lg text-muted-foreground">{state.cfdTitle || 'Destruction Request'}</p>
          <div className="inline-block bg-yellow-100 text-yellow-800 px-4 py-1 rounded-full text-sm font-medium">
            CfD Submitted — Awaiting Response
          </div>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-6">
          <div>
            <h3 className="font-semibold text-foreground mb-3">Process Timeline</h3>
            <div className="space-y-0">
              {timelineSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full mt-1 ${
                      step.status === 'complete' ? 'bg-green-500' :
                      step.status === 'active' ? 'bg-yellow-500' : 'bg-muted-foreground/30'
                    }`} />
                    {i < timelineSteps.length - 1 && <div className="w-0.5 h-8 bg-border" />}
                  </div>
                  <div className="pb-4">
                    <div className="font-medium text-foreground text-sm">{step.label}</div>
                    <div className="text-xs text-muted-foreground">{step.date}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Target Content</h3>
            <div className="text-sm space-y-1">
              <div><span className="text-muted-foreground">KB:</span> <span className="text-foreground">{state.targetKBName}</span></div>
              <div><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{state.contentLocation}</span></div>
              <div><span className="text-muted-foreground">Issue:</span> <span className="text-foreground">{state.issueTypeName}</span></div>
              <div><span className="text-muted-foreground">Severity:</span> <span className="text-foreground capitalize">{state.severity}</span></div>
            </div>
          </div>

          {state.hasBounty && (
            <div className="border-t border-border pt-4">
              <h3 className="font-semibold text-foreground mb-2">Token Forfeiture (if upheld)</h3>
              <div className="bg-destructive/10 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total Forfeiture:</span>
                  <span className="font-bold text-foreground">${bountyAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Giblet (Arbiter) — 1%:</span>
                  <span>${gibletShare.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Composer Community — 99%:</span>
                  <span>${communityShare.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-foreground mb-2">Possible Outcomes</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <div className="font-medium text-foreground">CfD Upheld</div>
                <div className="text-xs text-muted-foreground">Content removed/updated, tokens forfeited</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium text-foreground">CfD Dismissed</div>
                <div className="text-xs text-muted-foreground">Content remains, challenger notified</div>
              </div>
              <div className="p-3 bg-yellow-500/10 rounded-lg">
                <div className="font-medium text-foreground">Partial Resolution</div>
                <div className="text-xs text-muted-foreground">Content updated with disclaimer</div>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="font-medium text-foreground">No Response</div>
                <div className="text-xs text-muted-foreground">Proceeds to Arbiter decision</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Button onClick={() => navigate('/curt-channel')} className="min-w-[200px]">
            Back to CURT Channel
          </Button>
        </div>
      </div>
    </div>
  );
}
